/**
 * HustleSuite Disclaimer — /legal/disclaimer and /disclaimer.
 *
 * Real, honest, plain-English disclaimer that covers HustleFinder + the
 * whole HustleSuite ecosystem. This is the canonical Disclaimer linked
 * from the App Store submission for our iOS wrapper.
 */
import React from "react";
import { Link } from "react-router-dom";

const container = {
  maxWidth: 780,
  margin: "0 auto",
  padding: "64px 24px 96px",
  fontFamily: "-apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#111827",
  lineHeight: 1.65,
  fontSize: 16,
};

const h1   = { fontSize: 32, margin: "0 0 8px", fontWeight: 700 };
const h2   = { fontSize: 20, margin: "36px 0 8px", fontWeight: 600 };
const meta = { color: "#6b7280", fontSize: 14, marginBottom: 32 };
const p    = { margin: "0 0 14px" };
const ul   = { margin: "0 0 14px 20px", padding: 0 };

const CONTACT_EMAIL = "legal@hustlesuite.au";
const OPERATOR      = "LOCAL HUSTLE (ABN 16 160 565 775)";
const JURISDICTION  = "Queensland, Australia";
const LAST_UPDATED  = "23 February 2026";

export default function HustleSuiteDisclaimerPage() {
  return (
    <div style={container} data-testid="hustlesuite-disclaimer-page">
      <h1 style={h1}>HustleSuite Disclaimer</h1>
      <p style={meta}>Last updated: {LAST_UPDATED}</p>

      <p style={p}>
        HustleSuite (including HustleFinder, HustleTravel, HustleShop,
        HustlePlaza, HustleConnect, HustleMoments and every other module in
        the suite) is operated by {OPERATOR}. This Disclaimer explains what
        our apps and content do <em>not</em> guarantee, so you can decide
        whether to rely on them for your own decisions.
      </p>

      <h2 style={h2}>1. General information only</h2>
      <p style={p}>
        Everything you see in HustleSuite — search results, maps, hours,
        prices, reviews, ratings, place information, points of interest,
        product listings, promotional prices, editorial guides,
        AI-generated summaries, translations, videos and any other
        material — is provided for general information only. It is
        <strong> not </strong>professional advice, is not a substitute for
        checking the underlying source, and may be incomplete, out of
        date, or wrong.
      </p>

      <h2 style={h2}>2. Third-party data</h2>
      <p style={p}>
        HustleFinder and other modules combine data from many external
        sources, including but not limited to:
      </p>
      <ul style={ul}>
        <li>OpenStreetMap contributors (ODbL) — public toilets, drinking
            fountains, parks, transport stops, hospitals and similar POIs.</li>
        <li>Government open-data portals (GTFS transport feeds; council
            open datasets; ABS, ABR).</li>
        <li>Third-party APIs including Google Maps, Trivago, Amazon (via
            SerpAPI or PA-API), Dropshipzone, OpenChargeMap, and others.</li>
        <li>User-generated content submitted by other people through
            HustleMoments, HustleUp, reviews and comments.</li>
      </ul>
      <p style={p}>
        We do our best to keep this data fresh, but we cannot warrant that
        any specific place is open, that a bus is running on time, that
        prices shown are current, that a listing is accurate, or that a
        third-party service is available. Always confirm with the
        underlying provider before acting on what you see.
      </p>

      <h2 style={h2}>3. Not emergency, medical, legal, financial or
        professional advice</h2>
      <p style={p}>
        HustleSuite is <strong>not</strong> an emergency service. If you or
        someone you know is in danger, call 000 (Australia) or your local
        emergency number. Content on the app about hospitals, pharmacies,
        food safety, tenancy, taxes, business structures, superannuation,
        insurance, immigration, real estate valuation, home buying,
        conveyancing, health, nutrition, allergens, tides, weather, flood
        risk or fishing regulations is background information only. Speak
        with a qualified professional (doctor, lawyer, accountant,
        licensed real-estate agent, tax agent, migration agent, etc.)
        before making a decision that depends on that information.
      </p>

      <h2 style={h2}>4. Routing, maps and navigation</h2>
      <p style={p}>
        Route lines, ETAs, distances, driving instructions, EV charging
        stops, road-trip trails and check-in locations are estimates.
        Never rely on them alone while driving. Obey the road rules,
        obey signage, and use dedicated in-car navigation for turn-by-turn
        directions.
      </p>

      <h2 style={h2}>5. Property, valuation and flood data</h2>
      <p style={p}>
        HustleHydro flood scores, HustleProperty listings, planning
        overlays, elevation, easements, comparable sales and land-title
        excerpts are estimates or summaries derived from public sources.
        They are not a substitute for a licensed conveyancer, surveyor,
        town-planner, insurance broker or property valuer. Do not rely on
        them for buying, selling or lending decisions.
      </p>

      <h2 style={h2}>6. AI-generated content</h2>
      <p style={p}>
        Parts of the app use large-language models (Gemini, Claude, GPT
        variants) and image / video generators to summarise, translate,
        rewrite or illustrate content. AI output can be biased, invented
        or plain wrong. Treat every AI-generated summary as a starting
        point, not a final answer.
      </p>

      <h2 style={h2}>7. Reviews, ratings and testimonials</h2>
      <p style={p}>
        Reviews and testimonials come from real users but are personal
        opinion. We moderate them to remove spam, abuse and unsafe
        content, but we do not vouch for the accuracy of any specific
        review. Businesses may not agree with their reviews. If you
        believe a review breaches our Terms, contact us on{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2 style={h2}>8. Purchases, affiliate links & third-party
        sellers</h2>
      <p style={p}>
        Some links in HustleShop, HustleTackle and HustlePlaza are
        affiliate links. When you click through and buy, we may earn a
        commission at no extra cost to you. The seller (not us) is
        responsible for fulfilling the order, providing warranties, and
        handling returns. Prices, availability and shipping estimates
        shown are indicative — always confirm on the seller&apos;s own site
        before completing your purchase.
      </p>

      <h2 style={h2}>9. Accessibility</h2>
      <p style={p}>
        We aim to meet WCAG 2.2 AA across the app, but not every page is
        perfect yet. If you hit an accessibility barrier, email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we
        will prioritise a fix.
      </p>

      <h2 style={h2}>10. Limitation of liability</h2>
      <p style={p}>
        To the maximum extent permitted by Australian law (including the
        Australian Consumer Law), {OPERATOR} is not liable for any loss or
        damage arising from your use of HustleSuite, including loss of
        profit, revenue, data, reputation, or any indirect or
        consequential loss. This does not exclude any rights you have
        that cannot be lawfully excluded.
      </p>

      <h2 style={h2}>11. Changes to this Disclaimer</h2>
      <p style={p}>
        We may update this Disclaimer at any time. When we do, we will
        update the &ldquo;Last updated&rdquo; date at the top and, if the
        change is material, we will alert users in-app before the next
        session begins.
      </p>

      <h2 style={h2}>12. Contact</h2>
      <p style={p}>
        Questions about this Disclaimer? Email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. General
        support questions go through the{" "}
        <Link to="/support">Support form</Link>.
      </p>
      <p style={p}>
        Governing law: {JURISDICTION}.
      </p>
    </div>
  );
}
